import type { PropsWithChildren } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/auth-store';

interface Props {
  /** Optional role gate. Empty / unset means "any signed-in admin". */
  roles?: string[];
  /** Optional scope gate (permission_key). */
  scopes?: string[];
}

export function RequireAuth({ children, roles, scopes }: PropsWithChildren<Props>) {
  const location = useLocation();
  const session = useAuthStore((s) => s.session);
  const hasScope = useAuthStore((s) => s.hasScope);
  const hasAnyRole = useAuthStore((s) => s.hasAnyRole);

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (roles && roles.length > 0 && !hasAnyRole(...roles)) {
    return <ForbiddenPanel reason={`Requires role: ${roles.join(', ')}`} />;
  }
  if (scopes && scopes.length > 0 && !scopes.every((s) => hasScope(s))) {
    return <ForbiddenPanel reason={`Missing permission: ${scopes.join(', ')}`} />;
  }

  return <>{children}</>;
}

function ForbiddenPanel({ reason }: { reason: string }) {
  return (
    // Rendered inside the shell work area, which already supplies the gutter.
    <div className="panel flex max-w-lg flex-col items-start gap-2.5 p-6">
      <span className="badge badge-danger badge-dot">No access</span>
      <h2 className="text-[17px] font-semibold text-ink-900">You can't open this page</h2>
      <p className="text-[14px] text-ink-700">{reason}</p>
    </div>
  );
}
